import jwtDecode from 'jwt-decode';
import Cookies from 'universal-cookie';
import { Permission, UserDTO } from '../api/model';
import Api from '../api/api';
import Analytics from './user-analytics';
import SoarHelper from './soar-helper';

export default class UserHelper {
    private static cookies = new Cookies();
    private static COOKIE_IDENTIFIER = 'soar-id-token-v2';
    private static READ: Permission[] = [
        Permission.Read,
        Permission.Write,
        Permission.Review,
        Permission.Admin,
        Permission.Owner,
    ];
    private static WRITE: Permission[] = [Permission.Write, Permission.Review, Permission.Admin, Permission.Owner];
    private static ADMIN: Permission[] = [Permission.Admin, Permission.Owner];

    public static saveIdToken(idToken: string) {
        Analytics.setIsLoggedIn(true);
        const COOKIE_ID_TOKEN = `${this.COOKIE_IDENTIFIER}`;
        const options = {
            path: '/',
            secure: false,
        };
        this.cookies.set(COOKIE_ID_TOKEN, idToken, options);
    }

    public static validateAndGetIdToken(): string | undefined {
        const COOKIE_ID_TOKEN = `${this.COOKIE_IDENTIFIER}`;
        const idToken = this.cookies.get(COOKIE_ID_TOKEN);
        if (idToken) {
            const now = new Date().getTime();
            const decoded = jwtDecode(idToken);
            if (now > decoded.exp * 1000) {
                this.resetIdToken();
                return undefined;
            }
        }
        return idToken;
    }

    public static isLoggedIn(): boolean {
        return !!this.validateAndGetIdToken();
    }

    public static getDisplayUserName(user: UserDTO): string {
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }

        if (user.firstName && !user.lastName) {
            return `${user.firstName}`;
        }

        if (!user.firstName && user.name) {
            return user.name;
        }

        return 'Soar User';
    }

    public static validateUrl = (url: string | undefined) => {
        if (url && (!url.includes('https://') || !url.includes('http://'))) {
            if (url.includes('https://http://') || url.includes('http://https://')) {
                return (url = url.replace('https://http://', 'https://'));
            }

            if (url.includes('https://') || url.includes('http://')) {
                return url;
            }
            return (url = `https://${url}`);
        } else {
            return url ? url : undefined;
        }
    };

    public static resetIdToken() {
        const COOKIE_ID_TOKEN = `${this.COOKIE_IDENTIFIER}`;
        const options = {
            path: '/',
        };
        this.cookies.remove(COOKIE_ID_TOKEN, options);
    }
    public static getSubdomainPermission(user?: UserDTO): Permission {
        if (SoarHelper.isSoar()) {
            return Permission.Write;
        }
        if (user && user.domains) {
            const subdomain = Api.getSubdomain();
            const subdomainDTO = user.domains.find((domainPermissionDTO) => domainPermissionDTO.domain === subdomain);
            if (subdomainDTO) {
                return subdomainDTO.permission;
            }
        }
        return Permission.None;
    }

    public static isReputableUser(user?: UserDTO): boolean {
        return (user && user.reputable) || false;
    }

    public static hasReadPermissionsForSubdomain(user?: UserDTO): boolean {
        return this.READ.includes(this.getSubdomainPermission(user));
    }

    public static hasWritePermissionsForSubdomain(user?: UserDTO): boolean {
        return this.WRITE.includes(this.getSubdomainPermission(user));
    }

    public static hasAdminPermissionsForSubdomain(user?: UserDTO): boolean {
        return this.ADMIN.includes(this.getSubdomainPermission(user));
    }

    public static isEmailValid(email: string): boolean {
        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,8}$/i.test(email);
    }

    public static getFormattedAreaMapped(areaMapped: number) {
        if (areaMapped / 1000 / 1000 > 1000000) {
            return `${Math.round(areaMapped / 1000 / 1000 / 1000000).toLocaleString()} M km²`;
        } else {
            return `${Math.round(areaMapped / 1000 / 1000).toLocaleString()} km²`;
        }
    }

    public static isTwitterUrl = (val) =>
        val.match(/http(?:s)?:\/\/(?:www\.)?twitter\.com\/(@|[a-zA-Z0-9_])([a-zA-Z0-9_]+([a-zA-Z0-9_]|\/)$)/) ||
        val.match(/^twitter\.com\/(@|[a-zA-Z0-9_])([a-zA-Z0-9_]+([a-zA-Z0-9_]|\/)$)/) ||
        val.match(/^www\.twitter\.com\/(@|[a-zA-Z0-9_])([a-zA-Z0-9_]+([a-zA-Z0-9_]|\/)$)/) ||
        val.match(/^(@|[a-zA-Z0-9_])[a-zA-Z0-9_]+$/) ||
        val === '';

    public static isFacebookUrl = (val) =>
        val.match(/http(?:s)?:\/\/(?:www\.)?facebook\.com\/([a-z\d.]{5,}(a?|\/)$)/) ||
        val.match(/^facebook\.com\/([a-z\d.]{5,}(a?|\/)$)/) ||
        val.match(/^www\.facebook\.com\/([a-z\d.]{5,}(a?|\/)$)/) ||
        val.match(/^[a-z\d.]{5,}$/) ||
        val === '';

    public static isInstagramUrl = (val) =>
        val.match(/http(?:s)?:\/\/(?:www\.)?instagram\.com\/((?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}(a?|\/)$)/) ||
        val.match(/^instagram\.com\/((?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}(a?|\/)$)/) ||
        val.match(/^www\.instagram\.com\/((?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}(a?|\/)$)/) ||
        val.match(/^(?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}$/) ||
        val === '';

    public static isLinkedIn = (val) =>
        val.match(/http(?:s)?:\/\/(?:www\.)?linkedin\.com\/((?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}(a?|\/)$)/) ||
        val.match(/^linkedin\.com\/((?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}(a?|\/)$)/) ||
        val.match(/^www\.linkedin\.com\/((?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}(a?|\/)$)/) ||
        val.match(/^(?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}$/) ||
        val === '';

    public static isPasswordValid(password: string): boolean {
        return password.length >= 6;
    }

    public static setTwitterUrl = (twitterUrl: string) => {
        if (twitterUrl.indexOf('/') === -1) {
            return `https://twitter.com/${twitterUrl}`;
        } else if (UserHelper.isTwitterUrl(twitterUrl)) {
            return twitterUrl;
        } else if (twitterUrl.indexOf('/')) {
            const twitterHandle = twitterUrl.substring(twitterUrl.lastIndexOf('/') + 1);
            return `https://twitter.com/${twitterHandle}`;
        } else {
            return `https://twitter.com/${twitterUrl}`;
        }
    };

    public static setInstagramUrl = (instagramUrl: string) => {
        if (instagramUrl.indexOf('/') === -1) {
            return `https://www.instagram.com/${instagramUrl}`;
        } else if (UserHelper.isInstagramUrl(instagramUrl)) {
            return instagramUrl;
        } else if (instagramUrl.indexOf('/')) {
            const instagramHandle = instagramUrl.substring(instagramUrl.lastIndexOf('/') + 1);
            return `https://www.instagram.com/${instagramHandle}`;
        } else {
            return `https://www.instagram.com/${instagramUrl}`;
        }
    };

    public static setFacebookUrl = (facebookUrl: string) => {
        if (facebookUrl.indexOf('/') === -1) {
            return `https://www.facebook.com/${facebookUrl}`;
        } else if (UserHelper.isFacebookUrl(facebookUrl)) {
            return facebookUrl;
        } else if (facebookUrl.indexOf('/')) {
            const facebookHandle = facebookUrl.substring(facebookUrl.lastIndexOf('/') + 1);
            return `https://www.facebook.com/${facebookHandle}`;
        } else {
            return `https://www.facebook.com/${facebookUrl}`;
        }
    };

    public static setLinkedInUrl = (linkedInUrl: string) => {
        if (linkedInUrl.indexOf('/') === -1) {
            // Check if it is just a name and return as a profile
            return `https://www.linkedin.com/in/${linkedInUrl}`;
        } else {
            const profileRegex = /^(http(?:s)?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/i;
            const companyRegex = /^(http(?:s)?:\/\/)?(www\.)?linkedin\.com\/company\/[a-zA-Z0-9_-]+\/?$/i;

            const parsedUrl = new URL(linkedInUrl);
            const pathname = parsedUrl.pathname;

            // Check if is a profile and return
            if (profileRegex.test(linkedInUrl)) {
                return new URL(`https://www.linkedin.com${pathname}`).href;
                // Check if a company and return
            } else if (companyRegex.test(linkedInUrl)) {
                return new URL(`https://www.linkedin.com${pathname}`).href;
            } else {
                // The linkedin profile is invalid and populates with a message to reflect this
                return '';
            }
        }
    };

    public static getWordCount = (s: string) => {
        s = s.replace(/(^\s*)|(\s*$)/gi, ''); //exclude start and end white-space
        s = s.replace(/[ ]{2,}/gi, ' '); //2 or more space to 1
        s = s.replace(/\n /, '\n'); // exclude newline with a start spacing
        return s.split(' ').filter(function (str) {
            return str != '';
        }).length;
    };

    public static getUsersCurrency = (currency: string) => {
        if (currency === 'USD') {
            return 'US';
        } else {
            return currency;
        }
    };

    public static getTruncatedText = (text: string, length: number, ellipseText: boolean) => {
        if (text.length >= length) {
            return `${text.substring(0, length)}${ellipseText ? '...' : ''}`;
        } else {
            return text;
        }
    };

    public static getTagsFromAnnotationString = (annotationString: string) => {
        const boxSelectIcon =
            'data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M7.08505%2016H8.57905L7.27405%2014.02C7.16605%2013.831%206.96805%2013.615%206.86005%2013.543V13.525C6.96805%2013.453%207.16605%2013.282%207.27405%2013.102L8.42605%2011.563H6.93205L5.83405%2013.111H5.42005C5.46505%2012.85%205.48305%2012.58%205.48305%2012.301V9.7H4.15105V16H5.48305V14.605C5.48305%2014.407%205.45605%2014.173%205.42005%2013.939H5.83405L7.08505%2016ZM14.7678%2011.437C14.1738%2011.437%2013.6698%2011.671%2013.1478%2012.157C12.9858%2011.68%2012.5988%2011.437%2012.0048%2011.437C11.3838%2011.437%2010.8708%2011.707%2010.3128%2012.211L10.2228%2011.563H9.16082V16H10.4838V12.949C10.9698%2012.652%2011.1768%2012.562%2011.4648%2012.562C11.7978%2012.562%2011.9418%2012.688%2011.9418%2013.084V16H13.2198V12.958C13.7058%2012.661%2013.9128%2012.562%2014.2098%2012.562C14.5698%2012.562%2014.6778%2012.706%2014.6778%2013.084V16H16.0008V12.67C16.0008%2011.869%2015.5688%2011.437%2014.7678%2011.437ZM18.3402%208.971C18.0522%208.971%2017.5752%208.989%2016.9902%209.088L17.0262%209.844C17.5572%209.826%2017.8452%209.817%2018.2502%209.817C18.5742%209.817%2018.7632%209.844%2018.7362%2010.06C18.6912%2010.276%2018.4482%2010.582%2018.0162%2010.969L17.1072%2011.806C16.9902%2011.923%2016.9542%2012.058%2016.9542%2012.229V12.76H19.9602L19.9692%2011.896L18.7812%2011.878C18.5742%2011.878%2018.4302%2011.887%2018.2232%2011.923L19.1952%2011.041C19.6272%2010.627%2019.8432%2010.249%2019.8432%209.925C19.8432%209.133%2019.3482%208.971%2018.3402%208.971Z%22%20fill%3D%22white%22%2F%3E%0A%3Crect%20x%3D%222%22%20y%3D%222%22%20width%3D%2220%22%20height%3D%2220%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Ccircle%20cx%3D%2222%22%20cy%3D%222%22%20r%3D%222%22%20fill%3D%22white%22%2F%3E%0A%3Ccircle%20cx%3D%2222%22%20cy%3D%2222%22%20r%3D%222%22%20fill%3D%22white%22%2F%3E%0A%3Ccircle%20cx%3D%222%22%20cy%3D%222%22%20r%3D%222%22%20fill%3D%22white%22%2F%3E%0A%3Ccircle%20cx%3D%222%22%20cy%3D%2222%22%20r%3D%222%22%20fill%3D%22white%22%2F%3E%0A%3C%2Fsvg%3E%0A';
        const rulerIcon =
            'data:image/svg+xml,%3Csvg%20width%3D%2222%22%20height%3D%2222%22%20viewBox%3D%220%200%2022%2022%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M19.2053%203.437C18.6113%203.437%2018.1073%203.671%2017.5853%204.157C17.4233%203.68%2017.0363%203.437%2016.4423%203.437C15.8213%203.437%2015.3083%203.707%2014.7503%204.211L14.6603%203.563H13.5983V8H14.9213V4.949C15.4073%204.652%2015.6143%204.562%2015.9023%204.562C16.2353%204.562%2016.3793%204.688%2016.3793%205.084V8H17.6573V4.958C18.1433%204.661%2018.3503%204.562%2018.6473%204.562C19.0073%204.562%2019.1153%204.706%2019.1153%205.084V8H20.4383V4.67C20.4383%203.869%2020.0063%203.437%2019.2053%203.437Z%22%20fill%3D%22white%22%2F%3E%0A%3Cpath%20d%3D%22M1%201L21%2021H1V1Z%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M3.5%203.5L18.5%2018.5007H3.5V3.5Z%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M6.5%209.5L8%208%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M9%2012.5L10.5%2011%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M12%2015.5L13.5%2014%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E%0A';
        const arrowIcon =
            'data:image/svg+xml,%3Csvg%20width%3D%2222%22%20height%3D%2222%22%20viewBox%3D%220%200%2022%2022%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M1%2011H21%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M13%202L21%2011L13%2020%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E';
        const circleIcon =
            'data:image/svg+xml,%3Csvg%20width%3D%2222%22%20height%3D%2222%22%20viewBox%3D%220%200%2022%2022%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Ccircle%20cx%3D%2211%22%20cy%3D%2211%22%20r%3D%2210%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E%0A';
        const textFieldIcon =
            'data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M1%203.84211V1H10M19%203.84211V1H10M10%201V19M6.68421%2019H13.3158%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E%0A';
        const rectangleIcon =
            'data:image/svg+xml,%3Csvg%20width%3D%2222%22%20height%3D%2222%22%20viewBox%3D%220%200%2022%2022%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Crect%20x%3D%221%22%20y%3D%223%22%20width%3D%2220%22%20height%3D%2215%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E';
        const coordsIcon =
            'data:image/svg+xml,%3Csvg%20width%3D%2222%22%20height%3D%2222%22%20viewBox%3D%220%200%2022%2022%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Ccircle%20cx%3D%2211%22%20cy%3D%2211%22%20r%3D%228%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Ccircle%20r%3D%221%22%20transform%3D%22matrix%28-1%200%200%201%2011%2011%29%22%20fill%3D%22white%22%2F%3E%0A%3Cpath%20d%3D%22M11%201V5%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M11%2017V21%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M21%2011L17%2011%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M5%2011L1%2011%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E%0A';

        const boxSelectCount = UserHelper.countOccurrences(annotationString, 'boxSelect');
        const rulerCount = UserHelper.countOccurrences(annotationString, 'ruler');
        const arrowCount = UserHelper.countOccurrences(annotationString, 'arrow');
        // we can use 'circle' as a filter but have to divide by 2 otherwise radius is unique
        const circleCount = UserHelper.countOccurrences(annotationString, 'radius');
        const textFieldCount = UserHelper.countOccurrences(annotationString, 'textFields');
        const rectangleCount = UserHelper.countOccurrences(annotationString, 'rectangle');
        const coordsCount = UserHelper.countOccurrences(annotationString, 'latLngTool');

        // TODO: Data structure for this object
        const annotationTags = [
            { name: 'Box Select', count: boxSelectCount, icon: boxSelectIcon },
            { name: 'Ruler', count: rulerCount, icon: rulerIcon },
            { name: 'Arrow', count: arrowCount, icon: arrowIcon },
            { name: 'Circle', count: circleCount, icon: circleIcon },
            { name: 'Text Field', count: textFieldCount, icon: textFieldIcon },
            { name: 'Rectangle', count: rectangleCount, icon: rectangleIcon },
            { name: 'Coordinates', count: coordsCount, icon: coordsIcon },
        ];

        const filterAnnotationTags = annotationTags.filter((annotation) => (annotation.count !== 0 ? annotation : ''));
        return filterAnnotationTags;
    };

    private static countOccurrences = (string: string, word: string) => {
        return string.split('"').filter((el: string) => el.includes(word)).length;
    };

    // eslint-disable-next-line
    public static countAnnotations = (annotations: any[]) => {
        return annotations.reduce((accumulator, current) => accumulator + current.count, 0);
    };

    public static hasShareLinks = (userProfile: UserDTO) => {
        return (
            userProfile.websiteUrl ||
            userProfile.twitterUrl ||
            userProfile.facebookUrl ||
            userProfile.instagramUrl ||
            userProfile.linkedinUrl
        );
    };

    private static notEmptyOrUndefined = (value?: string | undefined | boolean) => {
        if (value === undefined || !value) return false;
        return true;
    };

    public static hasProfileDetails = (userDetails: UserDTO) => {
        if (this.notEmptyOrUndefined(userDetails.longDescription)) {
            return true;
        } else {
            return false;
        }
    };

    private static numberFromText = (text) => {
        const charCodes = text
            .split('')
            .map((char) => char.charCodeAt(0))
            .join('');
        return charCodes;
    };

    public static getDefaultAvatar = (userId: string): string => {
        // Count of png avatars in /assets/avatars
        const COUNT_AVATAR_IMAGES = 7;
        if (userId) {
            const selectAvatar = this.numberFromText(userId) % COUNT_AVATAR_IMAGES;
            return `/assets/avatars/Avatar${selectAvatar}.png`;
        }
        // Worst case return default avatar
        return `/assets/avatars/Avatar1.png`;
    };

    public static doAnnotationsExist = (annotations) => {
        if (!annotations) {
            return false;
        }

        // Although not ideal this fixes multiple problems with the annotations delete
        // Can be addressed when the annotations are overhauled
        // All we are doing is checking if the user has annotations
        const tags = UserHelper.getTagsFromAnnotationString(JSON.stringify(annotations));
        const annotationCount = UserHelper.countAnnotations(tags);

        if (annotationCount > 0) {
            return true;
        } else {
            return false;
        }
    };

    public static formatLayerText = (layerName: string) => {
        return layerName
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };
}
